import React, { useState, useMemo } from "react";
import styles from "./style.module.scss";
import AuthenticatedLayout from "../../../components/Layout/authenticatedLayout";
import FPAnalysisDataManager from "../../../components/DataManagers/FPAnalysisDataManager";
import DriverRunsChart from "../../../components/Timing/DriverRunsChart";
import TimingDataManager from "../../../components/DataManagers/TimingDataManager";
import ImportsDataManager from "../../../components/DataManagers/ImportsDataManager";
import { isEqual } from "lodash";
import { useSelector } from "react-redux/es/hooks/useSelector";
import { RootState } from "../../../state";
import DriverLoopsPlot, {
  LapTimeMode,
} from "../../../components/Timing/DriverLoopsPlot";
import GPSTrackMap from "../../../components/Timing/GPSTrackMap";
import PowerModeLeaderboard from "../../../components/Timing/PowerModeLeaderboard";
import TrackMapDataManager from "../../../components/DataManagers/TrackMapDataManager";
import DriverGPSDataManager from "../../../components/DataManagers/DriverGPSDataManager";
import RaceControl from "../../../components/Timing/RaceControl";
import SessionStatus from "../../../components/Timing/SessionStatus";
import SessionInfo from "../../../components/Timing/SessionInfo";
import LoopTrackMap from "../../../components/Timing/LoopTrackMap";
import TrackEvolution from "../../../components/Timing/TrackEvolution";
import { DriverPushLapEntry } from "../../../types/fpAnalysis";

// Load combined loops preference from localStorage
const useCombinedLoopsSave = localStorage.getItem("useCombinedLoops");
const useCombinedLoopsDefault = useCombinedLoopsSave
  ? useCombinedLoopsSave === "true"
  : true;

// Load lap time mode preference from localStorage
const lapTimeModeSave = localStorage.getItem("lapTimeMode");
const lapTimeModeDefault: LapTimeMode =
  (lapTimeModeSave as LapTimeMode) || "fastest";

type PeriodFilter = "wet" | "dry";

function FPAnalysisPage() {
  const [useCombinedLoops, setUseCombinedLoops] = useState(
    useCombinedLoopsDefault,
  );
  const [lapTimeMode, setLapTimeMode] =
    useState<LapTimeMode>(lapTimeModeDefault);
  const [selectedPowerMode, setSelectedPowerMode] = useState<"low" | "high">(
    "low",
  );
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>("dry");

  const driverRuns = useSelector(
    (state: RootState) => state.fpAnalysis.driverRuns,
    isEqual,
  );

  const isFullWetSession = useSelector(
    (state: RootState) => state.fpAnalysis.isFullWetSession,
  );

  const highlightWetPeriod = useSelector(
    (state: RootState) => state.fpAnalysis.highlightWetPeriod,
  );

  const wetPeriodRange = useSelector(
    (state: RootState) => state.fpAnalysis.wetPeriodRange,
  );

  // Derive push laps from driverRuns
  // In wet session mode, include all flying laps (not pit/out/in)
  const { driverAllPushLapsHighPower, driverAllPushLapsLowPower } =
    useMemo(() => {
      const highPower: Record<string, DriverPushLapEntry[]> = {};
      const lowPower: Record<string, DriverPushLapEntry[]> = {};

      Object.entries(driverRuns || {}).forEach(([driver, runs]) => {
        highPower[driver] = [];
        lowPower[driver] = [];

        runs.forEach((run) => {
          // Skip pit/out/in laps and invalid/incomplete laps
          if (
            run.duration === null ||
            run.valid === false ||
            run.type === "Pit" ||
            run.type === "Out" ||
            run.type === "In" ||
            run.type === "OutIn"
          ) {
            return;
          }

          // Filter by wet/dry period when highlightWetPeriod is enabled
          if (highlightWetPeriod) {
            const lapStartMinutes = run.startTime / 60000;
            const isInWetPeriod =
              lapStartMinutes >= wetPeriodRange[0] &&
              lapStartMinutes <= wetPeriodRange[1];

            if (periodFilter === "wet" && !isInWetPeriod) {
              return;
            }
            if (periodFilter === "dry" && isInWetPeriod) {
              return;
            }
          }

          // In wet session mode or wet period filter, include all flying laps
          // Otherwise only include push laps
          const shouldInclude =
            isFullWetSession ||
            highlightWetPeriod ||
            run.type === "HighPowerPush" ||
            run.type === "LowPowerPush";

          if (shouldInclude) {
            const entry: DriverPushLapEntry = {
              lapNumber: run.lapNumber || 0,
              lapTime: run.duration,
              loops: run.loopSectors || {},
            };

            if (run.highPower) {
              highPower[driver].push(entry);
            } else {
              lowPower[driver].push(entry);
            }
          }
        });
      });

      return {
        driverAllPushLapsHighPower: highPower,
        driverAllPushLapsLowPower: lowPower,
      };
    }, [
      driverRuns,
      isFullWetSession,
      highlightWetPeriod,
      wetPeriodRange,
      periodFilter,
    ]);

  const renderToggleControls = (id: string) => (
    <div className={styles.combinedLoopsToggle}>
      <label className={styles.radioOption}>
        <input
          type="checkbox"
          checked={useCombinedLoops}
          onChange={(e) => {
            setUseCombinedLoops(e.target.checked);
            localStorage.setItem(
              "useCombinedLoops",
              e.target.checked ? "true" : "false",
            );
          }}
        />
        Combined
      </label>
      <div className={styles.radioGroup}>
        {(["ultimate", "median", "fastest"] as LapTimeMode[]).map((mode) => (
          <label key={mode} className={styles.radioOption}>
            <input
              type="radio"
              name={`lapTimeMode-${id}`}
              value={mode}
              checked={lapTimeMode === mode}
              onChange={() => {
                setLapTimeMode(mode);
                localStorage.setItem("lapTimeMode", mode);
              }}
            />
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </label>
        ))}
      </div>
      {highlightWetPeriod && (
        <select
          className={styles.periodSelector}
          value={periodFilter}
          onChange={(e) => setPeriodFilter(e.target.value as PeriodFilter)}
        >
          <option value="dry">Dry</option>
          <option value="wet">Wet</option>
        </select>
      )}
    </div>
  );

  return (
    <AuthenticatedLayout title={"FP Analysis"} showJaguarLogo={false}>
      <FPAnalysisDataManager />
      <TimingDataManager />
      <TrackMapDataManager />
      <ImportsDataManager />
      <DriverGPSDataManager />
      <div className={styles.container}>
        <div className={styles.row1}>
          <DriverRunsChart />
          <div className={styles.gpsColumn}>
            <RaceControl />
            <div className={styles.statusRow}>
              <SessionInfo />
              <SessionStatus />
            </div>
            <GPSTrackMap />
          </div>
        </div>
        {/* Normal layout for large screens */}
        <div className={styles.row2}>
          <div className={styles.loopColumn}>
            {renderToggleControls("main")}
            <DriverLoopsPlot
              driverAllPushLaps={driverAllPushLapsLowPower}
              powerModeLabel="Low Power"
              useCombinedLoops={useCombinedLoops}
              lapTimeMode={lapTimeMode}
            />
            <DriverLoopsPlot
              driverAllPushLaps={driverAllPushLapsHighPower}
              powerModeLabel="High Power"
              useCombinedLoops={useCombinedLoops}
              lapTimeMode={lapTimeMode}
            />
          </div>
          <div className={styles.leaderboardColumn}>
            <PowerModeLeaderboard
              driverAllPushLaps={driverAllPushLapsLowPower}
              powerModeLabel="Low Power"
              useCombinedLoops={useCombinedLoops}
            />
            <div className={styles.mapsInColumn}>
              <LoopTrackMap useCombinedLoops={useCombinedLoops} />
            </div>
          </div>
          <div className={styles.leaderboardColumn}>
            <PowerModeLeaderboard
              driverAllPushLaps={driverAllPushLapsHighPower}
              powerModeLabel="High Power"
              useCombinedLoops={useCombinedLoops}
            />
            <div className={styles.mapsInColumn}>
              <TrackEvolution />
            </div>
          </div>
        </div>

        {/* Small screen layout - single row with selectable leaderboard */}
        <div className={styles.row2Small}>
          <div className={styles.loopColumn}>
            {renderToggleControls("small")}
            <DriverLoopsPlot
              driverAllPushLaps={driverAllPushLapsLowPower}
              powerModeLabel="Low Power"
              useCombinedLoops={useCombinedLoops}
              lapTimeMode={lapTimeMode}
            />
            <DriverLoopsPlot
              driverAllPushLaps={driverAllPushLapsHighPower}
              powerModeLabel="High Power"
              useCombinedLoops={useCombinedLoops}
              lapTimeMode={lapTimeMode}
            />
          </div>
          <div className={styles.smallLeaderboardColumn}>
            <div className={styles.powerModeSelector}>
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="powerModeSelect"
                  checked={selectedPowerMode === "low"}
                  onChange={() => setSelectedPowerMode("low")}
                />
                Low Power
              </label>
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="powerModeSelect"
                  checked={selectedPowerMode === "high"}
                  onChange={() => setSelectedPowerMode("high")}
                />
                High Power
              </label>
            </div>
            {selectedPowerMode === "low" ? (
              <PowerModeLeaderboard
                driverAllPushLaps={driverAllPushLapsLowPower}
                powerModeLabel="Low Power"
                useCombinedLoops={useCombinedLoops}
              />
            ) : (
              <PowerModeLeaderboard
                driverAllPushLaps={driverAllPushLapsHighPower}
                powerModeLabel="High Power"
                useCombinedLoops={useCombinedLoops}
              />
            )}
          </div>
          <div className={styles.smallMapsColumn}>
            <LoopTrackMap useCombinedLoops={useCombinedLoops} />
            <TrackEvolution />
          </div>
        </div>

        <div className={styles.row3}>
          <LoopTrackMap useCombinedLoops={useCombinedLoops} />
          <TrackEvolution />
        </div>
      </div>
    </AuthenticatedLayout>
  );
}

export default FPAnalysisPage;
